import type { App, Item } from "@/services/fobizzToolsApi";
import { AppEnum } from "@/services/fobizzToolsApi";
import { useAppsStore } from "@/stores/apps";
import type { ToolDefinition } from "@/types/tools";
import type { RouterLinkProps } from "vue-router";

/**
 * Composable for managing tool-related routing and URL generation
 */
export function useToolRouting() {
  const appsStore = useAppsStore();

  /**
   * Generate a URL for showing a specific tool item
   */
  const getShowUrl = (
    appId: AppEnum,
    itemId: string
  ): RouterLinkProps["to"] | undefined => {
    const app = appsStore.findAppById(appId);

    if (!app?.namespace) {
      console.warn(`Unable to generate show URL: App ${appId} not found`);
      return undefined;
    }

    return {
      name: `${app.namespace}-show`,
      params: { id: itemId },
    } as RouterLinkProps["to"];
  };

  /**
   * Generate a URL for the tool's info page
   */
  const getInfoUrl = (appId: AppEnum): RouterLinkProps["to"] | undefined => {
    const app = appsStore.findAppById(appId);

    if (!app?.namespace) {
      console.warn(`Unable to generate info URL: App ${appId} not found`);
      return undefined;
    }

    return {
      name: `${app.namespace}-info`,
    } as RouterLinkProps["to"];
  };

  /**
   * Generate a client path URL for a tool, including query parameters if provided
   */
  const getClientPath = (
    entity: App | ToolDefinition | Item
  ): string | undefined => {
    if (!entity.clientPath) {
      console.warn(`Unable to generate client path: Entity has no clientPath`, {
        entity,
      });
      return undefined;
    }

    // If it's a tool definition, handle query parameters
    if ("source" in entity && entity.source === "frontend") {
      const basePath = `/${entity.clientPath}`;
      if (!entity.queryParams) {
        return basePath;
      }

      const searchParams = new URLSearchParams();
      Object.entries(entity.queryParams).forEach(([key, value]) => {
        if (value) {
          searchParams.append(key, value);
        }
      });

      const queryString = searchParams.toString();
      return queryString ? `${basePath}?${queryString}` : basePath;
    }

    // For items and apps, ensure the path is absolute (starts with a slash)
    return entity.clientPath.startsWith("/")
      ? entity.clientPath
      : `/${entity.clientPath}`;
  };

  return {
    getShowUrl,
    getInfoUrl,
    getClientPath,
  };
}
